import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ContentIdea } from '@/types/dashboard';
import { logSupabaseError, logError } from '@/utils/sentry';

// Simple in-memory cache to prevent refetching on navigation
const generatedCache: { userId: string | null; data: ContentIdea[]; timestamp: number } | null = { userId: null, data: [], timestamp: 0 };
const CACHE_DURATION = 60000; // 1 minute cache

/** Invalidate the generated content cache to force a refetch on next access */
export const invalidateGeneratedCache = () => {
  if (generatedCache) {
    generatedCache.timestamp = 0;
    generatedCache.data = [];
  }
};

export const useGeneratedContent = (userId: string | null) => {
  const [isLoading, setIsLoading] = useState(() => {
    if (generatedCache && generatedCache.userId === userId && generatedCache.data.length > 0) {
      return false;
    }
    return true;
  });
  const [generatedIdeas, setGeneratedIdeas] = useState<ContentIdea[]>(() => {
    if (generatedCache && generatedCache.userId === userId) {
      return generatedCache.data;
    }
    return [];
  });
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  
  const { toast } = useToast();

  const fetchGeneratedContent = useCallback(async (isBackgroundRefresh = false) => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    // Only show loading on initial fetch, not on background refreshes
    if (!isBackgroundRefresh) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('user_generated_content')
        .select('*')
        .eq('user_id', userId)
        .or('is_trashed.is.null,is_trashed.eq.false')
        .order('created_at', { ascending: false });

      if (fetchError) {
        // Ignore transient network errors during navigation (Load failed)
        const isTransientError = fetchError.message?.includes('Load failed') || fetchError.message?.includes('aborted');
        if (!isTransientError) {
          logSupabaseError('Generated content: fetch', fetchError);
          setError('Failed to load generated content');
        }
        return;
      }

      const ideas: ContentIdea[] = (data || []).map((item) => ({
        id: item.id,
        user_id: item.user_id,
        source_scraped_content_id: null,
        title: item.title,
        description: item.description,
        content: item.content,
        edited_content: item.edited_content,
        platform: item.platform,
        viral_score: null, // user_generated_content doesn't have viral_score
        hashtags: null, // user_generated_content doesn't have hashtags
        created_at: item.created_at,
        updated_at: item.updated_at,
        is_favorited: item.is_favorited || false,
        favorited_at: item.favorited_at,
        is_used: item.is_used || false,
        used_at: item.used_at,
        is_trashed: item.is_trashed,
        trashed_at: item.trashed_at,
        alt_hooks: item.alt_hooks,
        text_overlays: item.text_overlays,
        improvements_applied: item.improvements_applied,
        perplexity_insights: item.perplexity_insights,
        source_competitor: null,
        source: 'user_generated' as const,
        main_idea: item.main_idea
      }));

      setGeneratedIdeas(ideas);
      setLastRefresh(new Date());
      
      // Update cache
      generatedCache.userId = userId;
      generatedCache.data = ideas;
      generatedCache.timestamp = Date.now();
      
    } catch (error) {
      logError('Generated content: unexpected fetch error', error, { hook: 'useGeneratedContent' });
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const refresh = useCallback(() => {
    fetchGeneratedContent(true); // Pass true for background refresh
  }, [fetchGeneratedContent]);

  // Initial fetch - skip if cache is fresh
  useEffect(() => {
    if (!userId) return;
    
    const isCacheFresh = generatedCache.userId === userId && 
      generatedCache.data.length > 0 && 
      (Date.now() - generatedCache.timestamp) < CACHE_DURATION;
    
    if (!isCacheFresh) {
      fetchGeneratedContent();
    }
  }, [userId, fetchGeneratedContent]);

  return {
    isLoading,
    generatedIdeas,
    error,
    lastRefresh,
    refresh
  };
};
